//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { darkBaseTheme, globalBaseTheme, lightBaseTheme, colors as globalColors } from '@tmt-insights/oms_fe_core/constants';
import logo from '../assets/national_geographic_logo.png';
// import previewPlaceholder from '../assets/preview_placeholder.svg';

// https://mui.com/customization/theming/

// primary, secondary, tertiary, success, error,info, warning, pending, background, action

export const colors = {
  light: deepmerge(globalColors.light, {
    primary: {
      main: '#FFC800',
      dark: '#e3a000',
      light: '#ffdf5d'
    },
    secondary: {
      main: '#33a9ac',
      dark: '#288789',
      light: '#5bbabc'
    },
    tertiary: {
      main: '#6041ba',
      dark: '#8464de',
      light: '#6045ad'
    },
    success: {
      main: '#4CBB17',
      dark: '#3c9512',
      light: '#5dc12e'
    },
    error: {
      main: '#F63710',
      dark: '#c42c0c',
      light: '#f75e3f'
    },
    warning: {
      main: '#ff860d',
      dark: '#e07000',
      light: '#fa952f'
    },
    pending: {
      main: '#ffa646',
      dark: '#cc8438',
      light: '#ffb76a'
    },
    // info: {
    //   main: '#40e0d0',
    //   dark: '#33b3a6',
    //   light: '#79e9de'
    // },
    background: {
      default: '#F5F5F5',
      paper: '#ffffff'
    },
    action: {
      focus: '#e8e8e8'
    }
  }),
  dark: deepmerge(globalColors.dark, {
    primary: {
      main: '#FFC800',
      dark: '#ffb300',
      light: '#ffdf5d'
    },
    secondary: {
      main: '#33a9ac',
      dark: '#288789',
      light: '#5bbabc'
    },
    tertiary: {
      main: '#6343c0',
      dark: '#643fcf',
      light: '#947cda'
    },
    success: {
      main: '#4CBB17',
      dark: '#3c9512',
      light: '#5dc12e'
    },
    error: {
      main: '#F63710',
      dark: '#c42c0c',
      light: '#f75e3f'
    },
    warning: {
      main: '#ffb61a',
      dark: '#d18f01',
      light: '#f9be40'
    },
    pending: {
      main: '#ffa646',
      dark: '#cc8438',
      light: '#ffb76a'
    },
    // info: {
    //   main: '#40e0d0',
    //   dark: '#33b3a6',
    //   light: '#79e9de'
    // },
    background: {
      paper: '#1c1d22',
      default: '#121315'
    },
    action: {
      focus: '#9898981a'
    }
  })
};

const globalTheme = deepmerge(globalBaseTheme, {
  shape: { borderRadius: 5 },
  customOverrides: {
    Header: {
      background: `#000000`,
      '& h1 img': {
        height: 35
      },
      '& h1': {
        color: 'rgba(255,255,255,.8)'
      },
      '& nav > ul': {
        '& a, & button': {
          fontWeight: 600,
          padding: '0.5rem 1rem',
          marginLeft: 10,
          color: 'rgba(255,255,255,.75)',
          transition: 'ease 250ms color',
          opacity: 1,
          '&:hover': {
            transition: 'ease 250ms color',
            color: 'rgba(255,255,255,.9)'
          }
        }
      }
    },
    Footer: {
      background: 'transparent'
    },
    JobFiles: {
      '& .MuiChip-root': {
        display: 'none'
      }
    },
    TitleBar: {},
    JobTimeline: {
      '& .MuiButton-textDefault': {
        color: 'var(--default-main)'
      }
    },
    TableStatus: { fontWeight: 700 },
    StatusCard: {
      label: {
        fontSize: '1.1rem'
      },
      '& $icon': {
        borderRadius: 50,
        padding: 8
      }
    },
    TypeLabel: {
      root: {
        boxShadow: 'none !important',
        border: 0,
        padding: '14px 4px',
        fontSize: '.9rem'
      }
    },
    AssetTotalStatus: {
      '& strong': {
        fontSize: '1.6rem',
        lineHeight: '1.8rem'
      }
    },
    ProgressBar: {
      '& .MuiLinearProgress-bar': {
        background: `linear-gradient(to right, #00c7bc 80%, #7521F4 100%)`
      }
    },
    Spinner: {
      boxShadow: `#d69163 0px 2px 2px, #574671 0px -2px 2px`
    },
    ProfileChipLabel: {
      color: '#ffffff'
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          color: '#000'
        }
      }
    },
    MuiBadge: {
      styleOverrides: {
        colorPrimary: {
          color: '#000',
          fontSize: '0.9rem'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        deleteIcon: {
          color: '#000',
          '&:hover': {
            color: '#111'
          }
        }
      }
    }
  }
});

const light = {
  palette: {
    mode: 'light',
    ...colors.light
  },
  customOverrides: {
    ...globalTheme.customOverrides,
    TitleBar: {
      background: '#ffffff',
      boxShadow: '0px 4px 18px rgba(40, 41, 61, 0.07)'
    },
    ExpandRow: {
      borderColor: colors.light.primary.light
    },
    StatusCardDefault: {
      '& h3, $count, $icon': {
        color: 'var(--default-dark)'
      },
      '& $icon': {
        backgroundColor: 'var(--default-light)'
      }
    },
    JobTitleBar: {
      '& label': {
        color: 'var(--default-dark)'
      }
    },
    TypeLabel: {
      root: {
        color: '#fff !important',
        boxShadow: 'none !important',
        fontSize: '.8rem'
      }
      // series: {
      //   backgroundColor: `${titleTypeColors.series} !important`
      // },
      // season: {
      //   backgroundColor: `${titleTypeColors.season} !important`
      // },
      // episode: {
      //   backgroundColor: `${titleTypeColors.episode} !important`
      // },
      // feature: {
      //   backgroundColor: `${titleTypeColors.feature} !important`
      // },
      // trailer: {
      //   backgroundColor: `${titleTypeColors.trailer} !important`
      // }
    },
    InventoryFile: {
      border: `1px solid #e6e0e0`,
      backgroundColor: '#ebebeb',
      '&.isSelected': {
        background: '#e6e0e0',
        borderTop: '1px solid #e9671042',
        borderRight: '1px solid #e9671042',
        borderBottom: '1px solid #e9671042'
      }
    },
    ButtonBar: {
      background: '#eaeaea'
    },
    TaskNoteReplyButton: {
      backgroundColor: colors.light.default.light
    },
    TitleVersionChip: {
      backgroundColor: 'transparent',
      color: colors.light.background.default
    }
    // FileIngestAnimatedBorder: {
    //   background: `conic-gradient(transparent, transparent, transparent, red, blue, transparent, transparent, transparent)`
    // }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--primary-main': colors.light.primary.main,
          '--primary-light': colors.light.primary.light,
          '--primary-dark': colors.light.primary.dark,
          '--secondary-main': colors.light.secondary.main,
          '--secondary-light': colors.light.secondary.light,
          '--secondary-dark': colors.light.secondary.dark,
          '--tertiary-main': colors.light.tertiary.main,
          '--tertiary-light': colors.light.tertiary.light,
          '--tertiary-dark': colors.light.tertiary.dark,
          '--default-main': colors.light.default.main,
          '--default-light': colors.light.default.light,
          '--default-dark': colors.light.default.dark,
          '--warning-main': colors.light.warning.main,
          '--warning-light': colors.light.warning.light,
          '--warning-dark': colors.light.warning.dark,
          '--success-main': colors.light.success.main,
          '--success-light': colors.light.success.light,
          '--success-dark': colors.light.success.dark,
          '--teal-main': colors.light.teal.main,
          '--teal-light': colors.light.teal.light,
          '--teal-dark': colors.light.teal.dark
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          color: 'var(--primary-dark)'
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          color: 'var(--primary-dark)'
        }
      }
    }
  }
};

const dark = {
  palette: {
    mode: 'dark',
    ...colors.dark
  },
  customOverrides: {
    ...globalTheme.customOverrides,
    TitleBar: {
      // background: '#0d0e10',
      background: `#070708`,
      borderTop: '1px solid #3b2d24'
    },
    FiltersBar: {},
    TypeLabel: {
      root: {
        color: '#000 !important',
        backgroundColor: '#9d9d9d',
        boxShadow: 'none !important',
        fontSize: '.8rem'
      }
      // series: {
      //   backgroundColor: `${titleTypeColors.series} !important`
      // },
      // season: {
      //   backgroundColor: `${titleTypeColors.season} !important`
      // },
      // episode: {
      //   backgroundColor: `${titleTypeColors.episode} !important`
      // },
      // feature: {
      //   backgroundColor: `${titleTypeColors.feature} !important`
      // },
      // trailer: {
      //   backgroundColor: `${titleTypeColors.trailer} !important`
      // }
    },
    ExpandRow: {},
    StatusCardDefault: {
      '& h3, $count, $icon': {
        color: '#a19da8'
      },
      '& $icon': {
        backgroundColor: '#a19da83a'
      }
    },
    TitleArea: {
      background: 'rgb(14 14 17)'
    },
    TitleSearch: {
      '& .MuiInputBase-root': {
        background: '#00000075'
      }
    },
    InventoryFile: {
      border: `1px solid #000`,
      backgroundColor: '#00000082',
      '&.isSelected': {
        background: '#000',
        borderTop: '1px solid #e9671042',
        borderRight: '1px solid #e9671042',
        borderBottom: '1px solid #e9671042'
      }
    },
    ButtonBar: {
      background: 'black'
    },
    TaskNoteReplyButton: {
      backgroundColor: colors.dark.default.dark
    },
    TitleVersionChip: {
      backgroundColor: 'transparent',
      color: colors.dark.background.default
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--primary-main': colors.dark.primary.main,
          '--primary-light': colors.dark.primary.light,
          '--primary-dark': colors.dark.primary.dark,
          '--secondary-main': colors.dark.secondary.main,
          '--secondary-light': colors.dark.secondary.light,
          '--secondary-dark': colors.dark.secondary.dark,
          '--tertiary-main': colors.dark.tertiary.main,
          '--tertiary-light': colors.dark.tertiary.light,
          '--tertiary-dark': colors.dark.tertiary.dark,
          '--default-main': colors.dark.default.main,
          '--default-light': colors.dark.default.light,
          '--default-dark': colors.dark.default.dark,
          '--warning-main': colors.dark.warning.main,
          '--warning-light': colors.dark.warning.light,
          '--warning-dark': colors.dark.warning.dark,
          '--success-main': colors.dark.success.main,
          '--success-light': colors.dark.success.light,
          '--success-dark': colors.dark.success.dark,
          '--teal-main': colors.dark.teal.main,
          '--teal-light': colors.dark.teal.light,
          '--teal-dark': colors.dark.teal.dark
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          color: 'var(--primary-main)'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        filledPrimary: {
          '& .MuiChip-label, & .MuiChip-icon': {
            color: '#000'
          }
        },
        defaultPrimary: {
          color: '#000',
          '& .MuiChip-label, & .MuiChip-icon': {
            color: '#000'
          }
        }
      }
    }
  }
};

const lightMergedWithGlobal = deepmerge(light, globalTheme);
const darkMergedWithGlobal = deepmerge(dark, globalTheme);

const lightTheme = createTheme(deepmerge(lightBaseTheme, lightMergedWithGlobal));
const darkTheme = createTheme(deepmerge(darkBaseTheme, darkMergedWithGlobal));

export default {
  darkTheme,
  lightTheme,
  logo
  // preview_placeholder: previewPlaceholder
};
